import { OperationOutcome, RelatedPerson } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

type Options = {
  patientId?: string;
  setOutcome?: (outcome: OperationOutcome) => void;
  refreshKey?: number;
};

// Stable empty array constant to prevent unnecessary re-renders
const EMPTY_RELATED_PEOPLE: RelatedPerson[] = [];

export const useRelatedPeople = (options?: Options): RelatedPerson[] => {
  const medplum = useMedplum();
  const { patientId } = useParams();
  const [relatedPeople, setRelatedPeople] = useState<RelatedPerson[]>([]);
  const [loading, setLoading] = useState(true);

  const id = patientId || options?.patientId;

  const setOutcome = options?.setOutcome;
  const refreshKey = options?.refreshKey;

  useEffect(() => {
    if (!id) {
      setRelatedPeople(EMPTY_RELATED_PEOPLE);
      setLoading(false);
      return;
    }

    let isCancelled = false;
    setLoading(true);
    medplum
      .searchResources('RelatedPerson', {
        patient: `Patient/${id}`,
      })
      .then((results) => {
        if (!isCancelled) {
          setRelatedPeople(results);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching related people:', error);
        if (setOutcome) {
          setOutcome({
            resourceType: 'OperationOutcome',
            issue: [
              {
                severity: 'error',
                code: 'exception',
                diagnostics: error.message,
              },
            ],
          });
        }
        setLoading(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [id, refreshKey, setOutcome, medplum]);

  if (loading) {
    return [];
  }

  return relatedPeople || [];
};
